// src/components/AssociatedClassesCard.jsx
import React from 'react';
import Card from './ui/Card';
import Button from './ui/Button';
import HelpIcon from './Documentation/HelpIcon';
import { getColorForCourse } from '../utils/colorUtils';

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
);

/**
 * AssociatedClassesCard Component
 * 
 * Displays a list of classes associated with a teacher or group.
 * Supports filtering by period and optional add button.
 * 
 * @param {Array} classes - Array of class objects to display
 * @param {string} filterPeriod - Selected period filter ('all' or period ID)
 * @param {Function} onFilterChange - Callback when filter changes
 * @param {Array} availablePeriods - Array of available periods for filtering
 * @param {Function} onAddClass - Optional callback for adding a new class
 * @param {string} emptyMessage - Message to show when no classes are found
 * @param {string} entityType - Type of entity ('teacher' or 'group')
 * @param {string} documentationId - ID for help documentation
 * @param {Array} hoveredCourseIds - Array of course IDs that are currently hovered
 */
function AssociatedClassesCard({
    classes = [],
    filterPeriod = 'all',
    onFilterChange,
    availablePeriods = [],
    onAddClass,
    emptyMessage = 'No classes scheduled.',
    entityType = 'teacher', // 'teacher' or 'group'
    documentationId = 'associated-classes',
    hoveredCourseIds = []
}) {
    return (
        <Card>
            <div className="p-4 space-y-3">
                <div className="flex justify-between items-center mb-2 border-b pb-2">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-700">
                            Associated Classes ({classes.length})
                        </h3>
                        <HelpIcon documentationId={documentationId} position="relative" />
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Period Filter */}
                        {onFilterChange && availablePeriods.length > 0 && (
                            <select
                                value={filterPeriod}
                                onChange={(e) => onFilterChange(e.target.value)}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                            >
                                <option value="all">All Periods</option>
                                {availablePeriods.map(period => (
                                    <option key={period.id} value={period.id}>
                                        {period.name}
                                    </option>
                                ))}
                            </select>
                        )}

                        {/* Add Button (for groups) */}
                        {onAddClass && (
                            <Button
                                variant="primary"
                                className="text-sm py-1 px-2 flex items-center space-x-1"
                                onClick={onAddClass}
                                title="Add New Class"
                            >
                                <PlusIcon /> <span>Add Class</span>
                            </Button>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {classes.length > 0 ? (
                        classes.map(cls => {
                            const isHovered = hoveredCourseIds.includes(cls.id);
                            const colorClass = getColorForCourse(
                                cls.course || cls.subject || cls.type
                            );

                            // Build tooltip
                            const tooltip = [
                                cls.course || cls.subject || cls.type,
                                entityType === 'teacher'
                                    ? (cls.groups && cls.groups.length > 0
                                        ? `Groups: ${cls.groups.map(g => typeof g === 'object' ? g.name : g).join(', ')}`
                                        : null)
                                    : (cls.teacher || 'No teacher'),
                                `${cls.day} ${cls.time}`,
                                cls.room ? `Room: ${cls.room}` : null
                            ].filter(Boolean).join('\n');

                            return (
                                <div
                                    key={cls.id}
                                    className={`p-2 rounded-md border text-xs transition-all duration-200 ${isHovered
                                            ? 'shadow-lg ring-2 ring-blue-500 ring-offset-2 scale-105 z-10'
                                            : 'shadow-sm'
                                        } ${colorClass}`}
                                    title={tooltip}
                                >
                                    <p className="font-semibold capitalize truncate">
                                        {cls.course || cls.subject || cls.type || 'N/A'}
                                    </p>

                                    {entityType === 'teacher' ? (
                                        // For teachers, show groups
                                        cls.groups && cls.groups.length > 0 && (
                                            <p className="truncate text-[11px] opacity-90">
                                                {cls.groups.map(g => typeof g === 'object' ? g.name : g).slice(0, 2).join(', ')}
                                                {cls.groups.length > 2 && ` +${cls.groups.length - 2}`}
                                            </p>
                                        )
                                    ) : (
                                        // For groups, show teacher
                                        <p className="truncate">{cls.teacher || 'N/A'}</p>
                                    )}

                                    <p className="text-[11px] opacity-80 truncate capitalize">
                                        {cls.day?.substring(0, 3)} {cls.time}
                                    </p>
                                    <p className="text-[11px] opacity-80 truncate">
                                        [{cls.room || 'N/A'}]
                                    </p>
                                </div>
                            );
                        })
                    ) : (
                        <p className="text-center text-gray-500 py-2 italic w-full">
                            {emptyMessage}
                            {filterPeriod !== 'all' && ' in the selected period'}
                        </p>
                    )}
                </div>
            </div>
        </Card>
    );
}

export default AssociatedClassesCard;

